import katex from 'katex'
import 'katex/dist/katex.min.css'
import type { ReactNode } from 'react'

/**
 * Текст от ИИ-учителя → разметка страницы.
 *
 * Формулы приходят в тегах <render>…</render> и рисуются KaTeX. В Telegram их
 * приходится превращать в картинки — здесь этого не нужно: формула остаётся
 * текстом, её можно выделить, скопировать и увеличить вместе со страницей.
 *
 * Правила чистки повторяют бота (app/handlers/messages.py): модель регулярно
 * пишет тег с лишними пробелами, путает доллары со скобками и обрывает формулу
 * на середине, когда генерация заканчивается по лимиту.
 */

/** Приводит все виды обёрток формул к одному тегу. */
function normalizeTags(text: string): string {
  return text
    // Тег <text>…</text> модель путает с \text{} внутри формулы — такого тега
    // нет, оставляем содержимое (так же в utils/formula_tags.py, правило §2.29).
    .replace(/<\s*text\s*>([\s\S]*?)<\s*\/\s*text\s*>/g, '$1')
    // Помесь тега с командой: «(<text{кДж}</text>)» (живой прогон 25 октября 2026).
    .replace(/<\s*text\s*\{([^{}<>]*)\}\s*(?:<\s*\/\s*text\s*>|>)?/g, '$1')
    .replace(/<\s*\/\s*text\s*>/g, '')
    .replace(/<\s*\/\s*render\s*>/g, '</render>')
    .replace(/<\s*render\s*>/g, '<render>')
    .replace(/\$\$([\s\S]+?)\$\$/g, '<render>$1</render>')
    // Одиночные доллары: содержимое не начинается и не кончается пробелом —
    // иначе «$5 и $10» про деньги превратились бы в формулу.
    .replace(/\$([^\s$](?:[^$]*?[^\s$])?)\$/g, '<render>$1</render>')
    .replace(/\\\[([\s\S]+?)\\\]/g, '<render>$1</render>')
    .replace(/\\\((.+?)\\\)/g, '<render>$1</render>')
}

/** Срезает хвост с незакрытым тегом формулы (генерация оборвалась). */
function closeUnclosed(text: string): string {
  const cut = text.lastIndexOf('<render>')
  if (cut !== -1 && !text.slice(cut).includes('</render>')) return text.slice(0, cut).trimEnd()
  return text
}

/** Десятичная запятая. В формуле запятая — знак препинания, и KaTeX ставит
 *  после неё пробел: «μ = 0,05» выходило «0, 05» (живой прогон 22 октября
 *  2026). Запятая в фигурных скобках — обычный символ. То же в боте:
 *  utils/renderer.decimal_comma. */
function decimalComma(tex: string): string {
  return tex.replace(/(\d),(?=\d)/g, '$1{,}')
}

function Formula({ tex, display }: { tex: string; display: boolean }) {
  let html: string
  try {
    html = katex.renderToString(decimalComma(tex), {
      displayMode: display,
      throwOnError: false,
      // trust=false запрещает команды, умеющие лезть за пределы формулы
      // (\href, \url, \includegraphics). Источник формул — вывод модели и
      // скраперы, доверять ему нельзя.
      trust: false,
      strict: false,
      output: 'html',
    })
  } catch {
    // KaTeX не осилил — показываем исходник моноширинным, как это делает бот
    // при отказе набора. Пустое место вместо формулы хуже сырой записи.
    return <code className="formula-fallback">{tex}</code>
  }
  return (
    <span
      className={display ? 'formula formula-block' : 'formula'}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

/** Разметка модели внутри абзаца: **жирный**, *курсив*, индексы <sub>/<sup>.
 *  plain — реплика ученика: её показываем как написана. «7800*S к 2700*2S» —
 *  умножение, а не курсив, и съесть звёздочки значит показать ученику не то,
 *  что он отправил (живой прогон 10 октября 2026). */
function inline(text: string, keyPrefix: string, plain = false): ReactNode[] {
  const out: ReactNode[] = []
  // Индексы модель иногда пишет HTML-тегами («m<sub>сп</sub>»), хотя ей велено
  // ставить их в формулу. Показываем индексом, а не сырой разметкой.
  const re = plain
    ? /<(sub|sup)>([^<]{1,40})<\/\1>/g
    : /\*\*(?!\s)(.+?)(?<!\s)\*\*|\*(?!\s)(.+?)(?<!\s)\*|<(sub|sup)>([^<]{1,40})<\/\3>/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const key = `${keyPrefix}m${i}`
    const тег = plain ? m[1] : m[3]
    const индекс = plain ? m[2] : m[4]
    if (тег === 'sub') out.push(<sub key={key}>{индекс}</sub>)
    else if (тег === 'sup') out.push(<sup key={key}>{индекс}</sup>)
    else if (m[1] !== undefined) out.push(<strong key={key}>{m[1]}</strong>)
    else out.push(<em key={key}>{m[2]}</em>)
    last = m.index + m[0].length
    i += 1
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Заголовки markdown и маркеры списка — в человеческий вид. */
function prepare(text: string): string {
  return text
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+(.+?)[ \t]*#*[ \t]*$/gm, '**$1**')
    // Только «*»: строку «- 273 °C» трогать нельзя, в физике это минус.
    .replace(/^([ \t]*)\*[ \t]+/gm, '$1• ')
    .replace(/<image>[\s\S]*?<\/image>/g, '')
}

// Границы жирного, внутри которого стоит формула. Обычный «**…**» разбирает
// inline() в пределах куска текста, но формула режет текст на куски, и пара
// звёздочек оказывалась по разные стороны — ученик видел «**Если … R … Почему?**».
const BOLD_OPEN = '\u0002'
const BOLD_CLOSE = '\u0003'
const BOLD_WITH_FORMULA = /\*\*(?!\s)((?:(?!\*\*)[^\n])*?<render>(?:(?!\*\*)[^\n])*?)(?<!\s)\*\*/g
// То же для курсива: «*Важное примечание: <render>…</render> …*» оставался со
// звёздочками по краям (живой прогон 10 октября 2026).
const EM_OPEN = '\u0004'
const EM_CLOSE = '\u0005'
const EM_WITH_FORMULA = /(?<!\*)\*(?![\s*])((?:(?!\*)[^\n])*?<render>(?:(?!\*)[^\n])*?)(?<![\s*])\*(?!\*)/g

/** Черта markdown («---») — разделитель, а не три дефиса. */
const RULE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/

/** plain — реплика ученика: формулы рисуем, разметку markdown не трогаем. */
export function RichText({ text, plain = false }: { text: string; plain?: boolean }) {
  const нормальный = closeUnclosed(normalizeTags(plain ? text : prepare(text))).trim()
  const prepared = plain
    ? нормальный
    : нормальный
        .replace(BOLD_WITH_FORMULA, `${BOLD_OPEN}$1${BOLD_CLOSE}`)
        .replace(EM_WITH_FORMULA, `${EM_OPEN}$1${EM_CLOSE}`)
  if (!prepared) return null

  const parts = prepared.split(/<render>([\s\S]*?)<\/render>/)
  // Своей строкой формула стоит, только когда в строке она одна. Прежде блоком
  // шла любая длиннее 28 знаков — и посреди фразы «…силу F_равн = 0,14 Н. Сложив…»
  // точка повисала одна на новой строке. Длинная формула в строке не теряется:
  // её прокручивает .formula (styles.css).
  const одна = (i: number) => {
    const до = parts[i - 1]
    const после = parts[i + 1]
    const начало = (i === 1 && !до.trim()) || /\n[ \t]*$/.test(до)
    const конец = (i === parts.length - 2 && !после.trim()) || /^[ \t]*\n/.test(после)
    return начало && конец
  }

  const nodes: ReactNode[] = []
  let жирный = false
  let курсив = false
  const выделить = (node: ReactNode, key: string) => {
    const сКурсивом = курсив ? <em key={`${key}e`}>{node}</em> : node
    return жирный ? <strong key={key}>{сКурсивом}</strong> : сКурсивом
  }

  parts.forEach((part, i) => {
    if (i % 2 === 1) {
      const tex = part.trim()
      if (!tex) return
      nodes.push(
        выделить(<Formula key={`f${i}`} tex={tex} display={одна(i)} />, `fb${i}`),
      )
      return
    }
    let кусок = part
    // Перевод строки рядом с выключной формулой уже дан её блоком — второй <br>
    // дал бы пустую строку.
    if (i > 0 && одна(i - 1)) кусок = кусок.replace(/^[ \t]*\n/, '')
    if (i < parts.length - 1 && одна(i + 1)) кусок = кусок.replace(/\n[ \t]*$/, '')
    if (!кусок) return
    кусок.split('\n').forEach((line, j, all) => {
      if (RULE.test(line)) {
        nodes.push(<hr key={`hr${i}-${j}`} className="rich-rule" />)
        return
      }
      line.split(/([\u0002-\u0005])/).forEach((piece, k) => {
        if (piece === BOLD_OPEN) жирный = true
        else if (piece === BOLD_CLOSE) жирный = false
        else if (piece === EM_OPEN) курсив = true
        else if (piece === EM_CLOSE) курсив = false
        else if (piece) {
          inline(piece, `t${i}-${j}-${k}-`, plain).forEach((node, n) =>
            nodes.push(выделить(node, `tb${i}-${j}-${k}-${n}`)),
          )
        }
      })
      if (j < all.length - 1) nodes.push(<br key={`br${i}-${j}`} />)
    })
  })

  return <div className="rich">{nodes}</div>
}
